import { useAuth0 } from '@auth0/auth0-react';
import { useState, useCallback, useRef, useEffect } from 'react';

import { getUploadUrl, uploadFileToS3 } from '../../../apis/fileApi';
import { createUploadedReceipt } from '../apis/uploadedReceiptApi';

export interface TrackedFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'creating' | 'done' | 'error';
  uploadProgress: number;
  receiptId?: string;
  error?: string;
}

/**
 * Processes files one by one: upload to S3 → create uploaded receipt.
 * Files start processing immediately when added.
 */
export const useReceiptUpload = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [trackedFiles, setTrackedFiles] = useState<TrackedFile[]>([]);
  const trackedFilesRef = useRef(trackedFiles);
  trackedFilesRef.current = trackedFiles;
  const isProcessingRef = useRef(false);

  const processNext = useCallback(async () => {
    if (isProcessingRef.current) return;

    const nextFile = trackedFilesRef.current.find(f => f.status === 'pending');
    if (!nextFile) return;

    isProcessingRef.current = true;
    const fileId = nextFile.id;
    const file = nextFile.file;

    try {
      const token = await getAccessTokenSilently();

      // Phase 1: Upload to S3
      setTrackedFiles(prev =>
        prev.map(f => f.id === fileId ? { ...f, status: 'uploading' as const } : f)
      );

      const { url, key } = await getUploadUrl(
        { fileName: file.name, contentType: file.type, folder: 'receipts' },
        token
      );

      await uploadFileToS3(url, file, progress => {
        setTrackedFiles(prev =>
          prev.map(f => f.id === fileId ? { ...f, uploadProgress: progress } : f)
        );
      });

      // Phase 2: Create uploaded receipt
      setTrackedFiles(prev =>
        prev.map(f => f.id === fileId ? { ...f, status: 'creating' as const, uploadProgress: 100 } : f)
      );

      const receipt = await createUploadedReceipt(
        {
          storageKey: key,
          contentType: file.type,
          originalFileName: file.name,
          fileSizeInBytes: file.size,
        },
        token
      );

      // Done
      setTrackedFiles(prev =>
        prev.map(f => f.id === fileId ? { ...f, status: 'done' as const, receiptId: receipt.id } : f)
      );
    } catch (err) {
      setTrackedFiles(prev =>
        prev.map(f =>
          f.id === fileId
            ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : 'Failed' }
            : f
        )
      );
    } finally {
      isProcessingRef.current = false;
    }
  }, [getAccessTokenSilently]);

  // Trigger processing whenever trackedFiles changes and there are pending files
  useEffect(() => {
    const hasPending = trackedFiles.some(f => f.status === 'pending');
    if (hasPending && !isProcessingRef.current) {
      processNext();
    }
  }, [trackedFiles, processNext]);

  const addFiles = useCallback((files: File[]) => {
    const pdfFiles = files.filter(f => f.type === 'application/pdf');
    if (pdfFiles.length === 0) return pdfFiles.length;

    const newTracked: TrackedFile[] = pdfFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      status: 'pending' as const,
      uploadProgress: 0,
    }));

    setTrackedFiles(prev => [...prev, ...newTracked]);
    return pdfFiles.length;
  }, []);

  const removeFile = useCallback((id: string) => {
    setTrackedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file && (file.status === 'pending' || file.status === 'error' || file.status === 'done')) {
        return prev.filter(f => f.id !== id);
      }
      return prev;
    });
  }, []);

  const reset = useCallback(() => {
    setTrackedFiles([]);
  }, []);

  const allDone = trackedFiles.length > 0 && trackedFiles.every(f => f.status === 'done');
  const isProcessing = trackedFiles.some(f => f.status === 'pending' || f.status === 'uploading' || f.status === 'creating');
  const receiptIds = trackedFiles.filter(f => f.status === 'done' && f.receiptId).map(f => f.receiptId as string);
  const hasErrors = trackedFiles.some(f => f.status === 'error');

  return {
    trackedFiles,
    addFiles,
    removeFile,
    reset,
    allDone,
    isProcessing,
    receiptIds,
    hasErrors,
  };
};
